import { useCallback, useState } from 'react';
import { useConstantCallback } from '@THC/hooks/useConstantCallback';

import { SNOWPLOW_EVENTS } from 'Services/analytics/analytics.const';
import { motorwayMovePageEvents } from 'Services/analytics/events/motorwayMoveEvents';
import { validatePhone } from 'Services/api/miscellaneous';
import useUser from 'Utilities/hooks/useUser';

import type { DiscoverMWMoveModalState } from '../DiscoverMWMove.types';

import type {
	RequestCallbackPayload,
	useRequestCallbackParams,
	useRequestCallbackReturn,
} from './ModalRequestCallback.types';

export const useRequestCallback = ({
	callback,
	defaultState = 'closed',
	withValidatePhone = true,
}: useRequestCallbackParams): useRequestCallbackReturn => {
	const [callbackModalState, setCallbackModalState] = useState<DiscoverMWMoveModalState>(defaultState);
	const user = useUser();

	const staticCallback = useConstantCallback(callback);

	const closeModal = useCallback(() => {
		motorwayMovePageEvents({
			name: 'Motorway Move Request a call back modal closed',
			schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
			version: SNOWPLOW_EVENTS.CLICK.VERSION,
		});
		setCallbackModalState('closed');
	}, []);

	const openModal = useCallback(() => setCallbackModalState('open'), []);

	const onSubmit = useCallback(
		async (payload: RequestCallbackPayload) => {
			motorwayMovePageEvents({
				name: 'Motorway Move Request a call back form submitted',
				schemaName: SNOWPLOW_EVENTS.CLICK.SCHEMA,
				version: SNOWPLOW_EVENTS.CLICK.VERSION,
			});
			setCallbackModalState('loading');

			const makeCall = async (data: RequestCallbackPayload) => {
				const { status } = await staticCallback(data);
				setCallbackModalState(status === 'success' ? 'success' : 'error');
			};

			try {
				if (withValidatePhone) {
					const { valid } = await validatePhone(payload.phone);
					if (valid === false) {
						setCallbackModalState('invalidPhone');
					} else {
						await makeCall(payload);
					}
				} else {
					await makeCall(payload);
				}
			} catch (error) {
				setCallbackModalState('error');
			}
		},
		[staticCallback, withValidatePhone],
	);

	return {
		closeModal,
		onSubmit,
		openModal,
		state: callbackModalState,
		user: {
			name: user.name,
			phone: user.phone,
		},
	};
};
